import * as React from "react";
import {connect} from "react-redux";
import {bindActionCreators} from "redux";

import {
  Notification,
    Pace
} from "components/Atoms";
import {Box, Container, Button} from "@mui/material";
import { H4, H5 } from "components/CustomTypography";
import { PhotoCamera } from '@mui/icons-material';

import * as Actions         from "actions/seller/seller.products";

import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PhotoUpload from "components/PhotoUpload";
import ModelTryOnSelector from "components/elements/seller/ModelTryOnSelector";
import * as ActionTypes from "../../../../../constants/ActionTypes";

const PrivateProductImageMain = (props) => {

    const {
        serviceError, reqStatus,
        isLoading, product, cancelEdit,
        addPrivateProductImage, removePrivateProductImage,
        productImageList,
        getSystemModels,
        generateModelTryOn,
        systemModels,
        systemModelsMeta,
        systemModelsLoadingMore,
        isLoadingModels,
        isGeneratingTryOn,
        imagesLightboxZIndex,
    } = props;

    const {prod_id, name, images: productImages} = product;
    const [showModelSelector, setShowModelSelector] = useState(false);
    const [modelTryOnGenderFilter, setModelTryOnGenderFilter] = useState('');
    const [imageList, setImageList] = useState(() =>
        Array.isArray(productImages) ? [...productImages] : []
    );
    /** Parent useEffect may dispatch GET_STORE_PRODUCTS_START before child effects run, clobbering reqStatus before SUCCESS is seen — sync from productImageList after our own upload/remove instead. */
    const awaitingImageListRef = useRef(false);

    const productImagesKey = useMemo(
        () => (product?.images || []).map((i) => i?.cdn?.public_id).join('|'),
        [product?.images]
    );

    const sendNotification = (type, { title, description = "" }) => {
        Notification[type]({
            message: title,
            description,
        });
    };

    useEffect(() => {
        setImageList(Array.isArray(product?.images) ? [...product.images] : []);
    }, [product?.prod_id, productImagesKey]);

    useEffect(() => {
        switch (reqStatus) {
            case ActionTypes.ADD_PRIVATE_PRODUCT_IMAGE_START:
            case ActionTypes.REMOVE_PRIVATE_PRODUCT_IMAGE_START:
                awaitingImageListRef.current = true;
                break;
            case ActionTypes.ADD_PRIVATE_PRODUCT_IMAGE_FAILURE:
            case ActionTypes.REMOVE_PRIVATE_PRODUCT_IMAGE_FAILURE:
                awaitingImageListRef.current = false;
                break;
            default:
                break;
        }
    }, [reqStatus]);

    useEffect(() => {
        if (!awaitingImageListRef.current || !Array.isArray(productImageList?.images)) return;
        setImageList([...productImageList.images]);
        awaitingImageListRef.current = false;
    }, [productImageList]);

    useEffect(() => {
        if (serviceError && reqStatus) {
            const feedback = {
                title: "Something went wrong!",
                description: serviceError.statusText,
            };
            sendNotification("error", feedback);
        }
    }, [serviceError, reqStatus]);

    const handleOpenModelSelector = () => {
        setModelTryOnGenderFilter('');
        getSystemModels({
            limit: Actions.SYSTEM_MODELS_PAGE_SIZE,
            skip: 0,
            append: false,
        });
        setShowModelSelector(true);
    };

    const handleModelTryOnGenderFilter = useCallback((nextGender) => {
        const g = typeof nextGender === 'string' ? nextGender : '';
        setModelTryOnGenderFilter(g);
        getSystemModels({
            limit: Actions.SYSTEM_MODELS_PAGE_SIZE,
            skip: 0,
            append: false,
            ...(g ? { gender: g } : {}),
        });
    }, [getSystemModels]);

    const handleLoadMoreSystemModels = useCallback(() => {
        const meta = systemModelsMeta || {
            total: 0,
            limit: Actions.SYSTEM_MODELS_PAGE_SIZE,
            nextSkip: 0,
            hasMore: false,
        };
        if (!meta.hasMore || isLoadingModels || systemModelsLoadingMore) return;
        getSystemModels({
            limit: meta.limit || Actions.SYSTEM_MODELS_PAGE_SIZE,
            skip: meta.nextSkip,
            append: true,
            ...(modelTryOnGenderFilter ? { gender: modelTryOnGenderFilter } : {}),
        });
    }, [systemModelsMeta, isLoadingModels, systemModelsLoadingMore, getSystemModels, modelTryOnGenderFilter]);

    const handleCloseModelSelector = () => {
        setShowModelSelector(false);
    };

    const handleSelectModel = (model) => {
        generateModelTryOn(prod_id, model);
        setShowModelSelector(false);
    };


    const addImageCallback = (file) => {
        const formData = new FormData();
        formData.append("image", file);
        addPrivateProductImage(product.prod_id, formData);
    }

    const removeImageCallback = (id) => {
        setImageList((prev) => prev.filter((image) => image.cdn.public_id !== id));
        removePrivateProductImage(product.prod_id, id);
    }

    return (
        <Container>
            {isLoading && <Pace color="#27ae60" height="2px" progressbar="2px" />}
            <Box>
                <hr />
                <Box width="100%" display="flex" alignItems="center" justifyContent="center">
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PhotoCamera />}
                        onClick={handleOpenModelSelector}
                        disabled={!imageList || imageList.length === 0}
                    >
                        Generate on Model
                    </Button>
                </Box>
                <hr />
                <PhotoUpload isRemoveAllowed={true}
                             keepFirstImage={true}
                             isDownloadAllowed={true}
                             images={imageList}
                             addImageCallback={addImageCallback}
                             removeImageCallback={removeImageCallback}
                             imagesLightboxZIndex={imagesLightboxZIndex}
                />
            </Box>
            
            <ModelTryOnSelector
                open={showModelSelector}
                onClose={handleCloseModelSelector}
                onSelectModel={handleSelectModel}
                models={systemModels}
                loading={isLoadingModels}
                loadingMore={systemModelsLoadingMore}
                hasMore={!!(systemModelsMeta && systemModelsMeta.hasMore)}
                onLoadMore={handleLoadMoreSystemModels}
                generating={isGeneratingTryOn}
                genderFilter={modelTryOnGenderFilter}
                onGenderFilterChange={handleModelTryOnGenderFilter}
            />
        </Container>
    );

};

// Mapping Props and State.
const _PrivateProductImageMain = connect(
    ({ products, bootupInfo }) => ({
      bootupInfo: bootupInfo,
      reqStatus: products.reqStatus,
      isLoading: products.isLoading,
      serviceError: products.serviceError,
      productImageList: products.productImageList,
      systemModels: products.systemModels,
      systemModelsMeta: products.systemModelsMeta || {
        total: 0,
        limit: 20,
        nextSkip: 0,
        hasMore: false,
      },
      systemModelsLoadingMore: !!products.systemModelsLoadingMore,
      isLoadingModels: products.isLoadingModels,
      isGeneratingTryOn: products.isGeneratingTryOn,
    }),
    (dispatch) => bindActionCreators({ ...Actions }, dispatch)
)(PrivateProductImageMain);

export { _PrivateProductImageMain as default };
